import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Flex, Spinner } from '@radix-ui/themes';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

import SectionCard from '../../../../shared/SectionCard/SectionCard.jsx';
import { useReportsPeakUsageHours } from '../data.js';
import { useCampaignFilter } from '../../hooks/useCampaignFilter.js';
import styles from './PeakUsageHours.module.css';

const CHART_COLOR = '#5c83fa';
const PEAK_COLOR = '#3a63d8';

const formatHour = hour => {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
};

const formatHourFull = hour => {
  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  return hour < 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`;
};

const buildChartOptions = (hours, peakHour) => ({
  chart: {
    type: 'column',
    height: 180,
    backgroundColor: 'transparent',
    style: { fontFamily: 'inherit' },
    spacing: [10, 0, 10, 0],
  },
  title: { text: null },
  legend: { enabled: false },
  credits: { enabled: false },
  exporting: { enabled: false },
  xAxis: {
    categories: hours.map(h => formatHour(h.hour)),
    lineColor: '#E8E8EC',
    tickLength: 0,
    labels: {
      style: { color: '#8B8D98', fontSize: '9px' },
      step: 2,
    },
    gridLineWidth: 0,
  },
  yAxis: {
    title: { text: null },
    gridLineColor: '#E8E8EC',
    gridLineDashStyle: 'Dash',
    min: 0,
    allowDecimals: false,
    labels: {
      style: { color: '#8B8D98', fontSize: '10px' },
    },
  },
  tooltip: {
    backgroundColor: '#1C2024',
    borderColor: '#1C2024',
    borderRadius: 10,
    shadow: false,
    style: { color: '#FFFFFF', fontSize: '13px' },
    formatter() {
      return `<b>${formatHourFull(
        this.point.hour,
      )}</b><br/><span style="font-size:15px;font-weight:700">${
        this.y
      }</span> conversations`;
    },
  },
  plotOptions: {
    column: {
      borderRadius: 3,
      borderWidth: 0,
      groupPadding: 0.05,
      pointPadding: 0.05,
    },
  },
  series: [
    {
      name: 'Conversations',
      data: hours.map(h => ({
        y: h.count,
        hour: h.hour,
        color: h.hour === peakHour ? PEAK_COLOR : CHART_COLOR,
        opacity: h.hour === peakHour ? 1 : 0.6,
      })),
    },
  ],
});

export const PeakUsageHours = ({ startDate, endDate }) => {
  const { t } = useTranslation('reports');
  const { campaignId } = useCampaignFilter();
  const { data, isLoading } = useReportsPeakUsageHours({
    campaignId,
    startDate,
    endDate,
  });

  const chartOptions = useMemo(
    () => buildChartOptions(data.hours, data.peakHour),
    [data.hours, data.peakHour],
  );

  const hasData = data.hours.some(h => h.count > 0);

  const header = (
    <Flex align="center" gap="2">
      <i
        className="fa-regular fa-clock"
        style={{ color: 'var(--color-purple)' }}
      />
      <Heading size="4" m="0">
        {t('summary.ai.peakHours')}
      </Heading>
    </Flex>
  );

  if (isLoading) {
    return (
      <SectionCard headerContent={header}>
        <Flex align="center" justify="center" py="6">
          <Spinner size="3" />
        </Flex>
      </SectionCard>
    );
  }

  if (!hasData) {
    return (
      <SectionCard headerContent={header}>
        <div className={styles.emptyState}>
          <i className={`fa-regular fa-clock ${styles.emptyIcon}`} />
          <p className={styles.emptyText}>{t('summary.ai.noPeakHours')}</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard headerContent={header}>
      <div className={styles.container}>
        <div className={styles.peakStat}>
          {t('summary.ai.busiestAt')}{' '}
          <span className={styles.peakBadge}>
            <i className="fa-regular fa-arrow-trend-up" />
            {data.peakHour != null && formatHourFull(data.peakHour)}
          </span>
          <span>
            ({data.peakCount} {t('summary.ai.totalConversations').toLowerCase()}
            )
          </span>
        </div>

        <div className={styles.chartWrapper}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      </div>
    </SectionCard>
  );
};
